import React, { useEffect, useRef, useState } from 'react';
import LoginScreen from './Login';
import MfaScreen from './MfaScreen';
import { submitActivate } from '../commons/util/activateFlow';
import engagebayBaseAPI from '../api';

export interface LoginDeps {
    [key: string]: any;
}

export interface MfaContext {
    securityType: string;
    redirectBase: string | null;
    /** Raw (unmasked) email — present when coming from the credential form; absent on mfa_pending resume. */
    email?: string;
}

/**
 * Login step state machine. Renders either LoginScreen (credentials) or MfaScreen (OTP).
 * Never renders both simultaneously — the MFA screen contains no credential-step UI.
 *
 * Why this exists: separating the step router from both screens keeps each screen
 * self-contained and removes the need to toggle DOM sections via jQuery.
 *
 * 'activating' step: when deps.exchange_token is present (post-OAuth redirect),
 * the component renders nothing and calls submitActivate() immediately. This
 * eliminates the JSESSION /oauth?user=cacheKey hop replaced in Phase 7.
 *
 * Browser back-button support: handleMfaRequired pushes a synthetic history entry
 * so the back button returns to the credentials form instead of jumping to /home.
 * A popstate listener (reading step via ref to avoid stale closure) catches the pop.
 * handleBack uses history.back() when we own the entry; falls back to a direct state
 * change for mfa_pending direct loads where we never pushed.
 *
 * bfcache guard: after OTP success the browser navigates to /home, but caches this
 * page in memory (Back/Forward Cache). Pressing back restores the full React state
 * (step='mfa') without re-running JS — bypassing deps.mfa_pending entirely.
 * The pageshow listener with persisted:true detects this and resets to credentials.
 */
type LoginStep = 'credentials' | 'mfa' | 'activating';

const LoginApp: React.FC<{ deps: LoginDeps }> = ({ deps }) => {
    const initialStep: LoginStep =
        deps.exchange_token ? 'activating' :
        deps.mfa_pending    ? 'mfa' :
        'credentials';
    const initialMfa: MfaContext | null = deps.mfa_pending
        ? { securityType: deps.securityType, redirectBase: deps.redirect_base || null, email: deps.email || undefined }
        : null;

    const [step, setStep] = useState<LoginStep>(initialStep);
    const [mfaContext, setMfaContext] = useState<MfaContext | null>(initialMfa);
    const stepRef = useRef<LoginStep>(initialStep);

    // Keep stepRef in sync so the popstate handler always reads the current step
    // without needing to re-register the listener on every render.
    useEffect(() => { stepRef.current = step; }, [step]);

    // When deps.exchange_token is present (post-OAuth redirect), call activate immediately.
    useEffect(() => {
        if (step === 'activating' && deps.exchange_token) {
            submitActivate(deps.exchange_token, deps.redirect_base || null);
        }
    }, []);

    // Browser back-button handler — registered once, reads live step via stepRef.
    useEffect(() => {
        function handlePopState() {
            if (stepRef.current === 'mfa') {
                showCredentials();
            }
        }
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // bfcache guard: browsers restore this page from memory on back-navigation without
    // re-executing JS. pageshow fires with persisted:true in that case — we reset to
    // credentials so the MFA screen never reappears after a successful login.
    useEffect(() => {
        function handlePageShow(e: PageTransitionEvent) {
            if (e.persisted) {
                setStep('credentials');
                setMfaContext(null);
            }
        }
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    function handleMfaRequired(securityType: string, redirectBase: string | null, email?: string) {
        // Push a synthetic history entry so back button lands on credentials, not /home.
        history.pushState({ step: 'mfa' }, '');
        setMfaContext({ securityType, redirectBase, email });
        setStep('mfa');
    }

    /**
     * Transitions to the credentials form and clears the server-side MFA cookie.
     *
     * Why cancel-mfa here: eb_mfa_token is HttpOnly so JS cannot clear it directly.
     * Without this call, a browser-back that triggers a full page reload would find the
     * cookie still alive, causing the server to set mfa_pending:true and loop back to
     * the MFA screen. Fire-and-forget is intentional — the UI transitions immediately.
     */
    function showCredentials() {
        engagebayBaseAPI.post('/rest/api/login/cancel-mfa').catch(() => {});
        setStep('credentials');
        setMfaContext(null);
    }

    function handleBack() {
        // If we pushed the synthetic entry, consume it via history.back() so the
        // browser's position stays consistent — popstate fires and calls showCredentials.
        // For mfa_pending direct loads (no push), call showCredentials directly.
        if (history.state?.step === 'mfa') {
            history.back();
        } else {
            showCredentials();
        }
    }

    function handleSuccess(exchangeToken: string | null, redirectBase: string | null, loginInfo?: any) {
        if (loginInfo) {
            growsumoInit(loginInfo);
        }
        const dl = (window as any).dataLayer;
        if (Array.isArray(dl)) {
            dl.push({ event: 'logIn' });
        }
        // Replace the synthetic MFA history entry with credentials before navigating away.
        // This ensures the back button from /home lands on the credentials form, not the
        // MFA screen, in browsers that don't bfcache or that restore with the replaced state.
        if (history.state?.step === 'mfa') {
            history.replaceState({ step: 'credentials' }, '');
        }
        // ?return= is forwarded by logout.jsp from ebLogout(). Pass directly to
        // submitActivate as deepLink — the backend appends it to /home in the activate
        // redirect, so React Router picks it up naturally on page load. No sessionStorage needed.
        const returnPath = new URLSearchParams(window.location.search).get('return');
        const deepLink = (returnPath && returnPath.startsWith('#')) ? returnPath : undefined;
        if (exchangeToken) {
            submitActivate(exchangeToken, redirectBase, deepLink ? { deepLink } : undefined);
        } else {
            window.location.href = '/home' + (deepLink || window.location.hash || '');
        }
    }

    if (step === 'activating') {
        return null;
    }

    if (step === 'mfa' && mfaContext) {
        return (
            <MfaScreen
                context={mfaContext}
                deps={deps}
                onSuccess={(token, base) => handleSuccess(token, base)}
                onBack={handleBack}
            />
        );
    }

    return (
        <LoginScreen
            deps={deps}
            onMfaRequired={handleMfaRequired}
            onSuccess={handleSuccess}
        />
    );
};

function growsumoInit(loginInfo: any) {
    try {
        const gs = (window as any).growsumo;
        if (!gs) return;
        gs.data.name = loginInfo.name;
        gs.data.email = loginInfo.email;
        gs.data.customer_key = loginInfo.email;
        gs.createSignup();
    } catch {
        // affiliate attribution failure is non-fatal
    }
}

export default LoginApp;
